"use client";

import React, { useRef, useState, useCallback, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@wtc/ui";

interface FloatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number; height: number };
  headerAction?: React.ReactNode;
}

/**
 * Floating Modal Component
 * - Draggable by the header
 * - Resizable
 * - Allows interaction with page behind it
 * - Persists position in localStorage
 */
export function FloatingModal({
  isOpen,
  onClose,
  title,
  children,
  initialPosition = { x: 100, y: 100 },
  initialSize = { width: 600, height: 700 },
  headerAction,
}: FloatingModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  // Load position from localStorage on mount
  useEffect(() => {
    const storageKey = `floating-modal-${title}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const { position: savedPos, size: savedSize } = JSON.parse(stored);
        setPosition(savedPos);
        setSize(savedSize);
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [title]);

  // Save position to localStorage
  const savePosition = useCallback(
    (pos: typeof position, sz: typeof size) => {
      const storageKey = `floating-modal-${title}`;
      localStorage.setItem(storageKey, JSON.stringify({ position: pos, size: sz }));
    },
    [title]
  );

  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!modalRef.current) return;

    const rect = modalRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  }, []);

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Keep modal within viewport
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - size.height;

      const constrainedX = Math.max(0, Math.min(newX, maxX));
      const constrainedY = Math.max(0, Math.min(newY, maxY));

      setPosition({ x: constrainedX, y: constrainedY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      savePosition(position, size);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset, position, size, savePosition]);

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height,
      });
    },
    [size]
  );

  // Handle resize move
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;

      const newWidth = Math.max(400, resizeStart.width + deltaX);
      const newHeight = Math.max(300, resizeStart.height + deltaY);

      setSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      savePosition(position, size);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, resizeStart, position, size, savePosition]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - allows interaction with page */}
      <div className="fixed inset-0 z-30" onClick={onClose} style={{ pointerEvents: "auto" }} />

      {/* Floating Modal */}
      <div
        ref={modalRef}
        className="fixed bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${size.width}px`,
          height: `${size.height}px`,
          zIndex: 40,
          pointerEvents: "auto",
        }}
      >
        {/* Header - Draggable */}
        <div
          ref={headerRef}
          onMouseDown={handleDragStart}
          className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950/40 dark:to-blue-900/40 border-b border-gray-200 dark:border-gray-700 rounded-t-lg cursor-move hover:bg-gradient-to-r hover:from-blue-100 hover:to-blue-200 dark:hover:from-blue-900/40 dark:hover:to-blue-800/40 transition-colors"
          style={{ userSelect: "none" }}
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <div className="flex items-center gap-2">
            {headerAction}
            <Button variant="ghost" size="sm" onClick={onClose} className="p-1 h-auto">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">{children}</div>

        {/* Resize Handle */}
        <div
          onMouseDown={handleResizeStart}
          className="absolute bottom-0 right-0 w-6 h-6 bg-blue-500 rounded-tl cursor-se-resize hover:bg-blue-600 transition-colors"
          style={{
            pointerEvents: "auto",
          }}
          title="Drag to resize"
        />
      </div>
    </>
  );
}
